// The whole of this client's state for one plan, and the rules about when it changes.
//
// ---------------------------------------------------------------------------
// FAILURE IS ABOUT A REQUEST, NEVER ABOUT TIME
// ---------------------------------------------------------------------------
//
// `status` moves to something other than `ready` only while there has never been a
// snapshot. Once a plan has been read once, a later failure — a dropped network, a signal
// that arrived and whose refetch fell over — leaves the last snapshot on screen and
// changes nothing else. Nothing in this file consults a clock for a decision about what to
// render, and there is no timer whose firing changes anything by itself: the poll below
// reads, and only what it reads can change the screen.
//
// That is what makes the silence case fall out rather than have to be special-cased: the
// state machine has no edge that time can take.
//
// ---------------------------------------------------------------------------
// REFETCHES DO NOT RACE
// ---------------------------------------------------------------------------
//
// Signals arrive while a refetch is in flight, and a slow first response must not land on
// top of a fast second one. Two mechanisms, both small:
//
//   * every load carries a generation number, and a result whose generation is stale is
//     dropped rather than applied;
//   * a signal arriving during a load sets a flag rather than starting a second load, and
//     one more load runs when the first finishes. A burst of six signals costs two reads,
//     and the second is guaranteed to have started after the last of them.
//
// ---------------------------------------------------------------------------
// POSTING
// ---------------------------------------------------------------------------
//
// POST, insert the message the SERVER returned, refetch. The middle step is the only
// optimistic state this client holds and it holds nothing the server did not say — see
// `afterPost`. The refetch is unconditional and does not wait for a signal: the signal
// will come, and a person who has just pressed send should not be watching for it.
//
// ---------------------------------------------------------------------------
// WHY THERE IS A CLOCK IN THIS FILE AFTER ALL, AND WHAT IT IS ALLOWED TO DO
// ---------------------------------------------------------------------------
//
// `facilitatorBusy` is never pushed. The delivery layer's change fingerprint deliberately
// excludes the lease columns, so a worker taking a lease on this plan signals nobody. The
// consequence is asymmetric and it is the awkward half that matters:
//
//   * busy -> not busy travels for free, because the thing that ends an activation is
//     usually the facilitator writing something, and writing something IS a change;
//   * not busy -> busy does not travel at all. Without something asking, the indicator
//     appears at the next fetch the client happened to make — which, after somebody sends
//     a message, is the refetch that runs immediately after the POST, before any worker
//     has claimed the plan. So the indicator would essentially never be seen during the
//     one minute it exists for.
//
// An indicator that reliably fails to appear is worse than none: it is dead code that
// looks like a feature, and the next person to work on this would "fix" it with a spinner.
// So this client polls.
//
// Two things about how, both deliberate.
//
//   * IT IS A FULL REFETCH, not a plan read patched into the snapshot. A plan object from
//     15 seconds after the messages beside it is a client holding two moments at once,
//     which is the merge this design forbids wearing a smaller hat. The whole snapshot is
//     replaced or nothing is. Six small reads on a fifteen-second timer, for a family, is
//     nothing; "at this volume it is cheaper to be simple than to be clever".
//
//   * IT IS UNCONDITIONAL — a fixed interval while this plan is on screen and the tab is
//     visible. NOT "poll while a reply is outstanding". The tempting version needs the
//     client to hold the idea of an outstanding reply, which is elapsed-time state about
//     silence, and elapsed-time state about silence is the thing that becomes "still
//     waiting…" and then becomes a timeout. There is no such variable here, so there is
//     nothing for a later change to escalate. A tick reads; what it reads decides what is
//     on screen; the tick itself never changes anything.
//
// The residual, stated plainly: the indicator can be up to one interval late, and a short
// activation that starts and finishes between two ticks is never shown at all. That is the
// right way round. A missed "considering" costs a family nothing; the alternative — a
// client that infers busy from having sent a message — is a client asserting the
// facilitator is thinking when it may not be, in the one place where being wrong about
// that is unkind. Polling also doubles as the backstop for a dropped signal stream.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { Message, Thread } from '../api/contract.js';
import { defaultInternals, type Internals } from '../internals.js';
import { session } from '../session.js';
import type { PlanSnapshot } from './snapshot.js';

/**
 * `waiting` is a read that failed because the bearer is stale — the vendor's next token
 * publish cures exactly that, so the screen may honestly say it is signing back in.
 * `faltered` is every other retryable failure — a rate limit, a server error, a response
 * that would not decode — which retries on the same triggers but must not claim to be
 * about signing in: a plan stuck on a 500 saying "signing you back in" is a lie that
 * never expires.
 */
export type PlanStatus =
  'loading' | 'ready' | 'refused' | 'unreachable' | 'waiting' | 'faltered';

export type PlanState = {
  status: PlanStatus;
  snapshot: PlanSnapshot | null;
  posting: boolean;
  /** The last send did not go through. Cleared by the next attempt. */
  sendFailed: boolean;
  refetch: () => void;
  loadOlder: () => void;
  post: (body: string) => Promise<void>;
  /**
   * Resolves a confirmation. Who it did not write to (issue #155, D96) is not returned
   * here — it used to be, read once by `PendingActionRow.tsx` at the moment of
   * approving, before the next refetch removed the row it was shown on. That reporting
   * now lives on the confirmation's own system message instead, which stays in the
   * thread rather than flashing and vanishing with the row.
   */
  resolve: (actionId: string, resolution: string) => Promise<void>;
  /**
   * Reports the reader's own read position on a thread. Fire-and-forget from the caller's
   * side — see the definition for what "immediately" means here and why it still needs the
   * load generation counter. `newestKnownSeq` is the caller's — `plan/useMarkThreadRead.ts`
   * — own live answer to "what is the newest message I currently know about for this
   * thread", read fresh when the response lands rather than assumed from `seq` itself.
   */
  markRead: (
    threadId: string,
    seq: string,
    newestKnownSeq: () => string | undefined,
  ) => void;
};

export type UsePlanOptions = {
  client: ApiClient;
  planId: string;
  internals?: Internals;
  /** The signal stream and the poll, both off, for tests that drive refetches by hand. */
  live?: boolean;
  /** How often the plan is re-read so `facilitatorBusy` can appear. 0 disables it. */
  pollMs?: number;
};

/**
 * Fifteen seconds.
 *
 * Long enough that it is not a load, short enough that "Mor is reading" shows up while it
 * still describes something. It is not tuned to the lease — `DEFAULT_LEASE_MS` is
 * forty-five seconds and an activation is expected to run for seconds to minutes —
 * because the interval is about how late the indicator may be, not about how long a lease
 * lasts.
 */
export const DEFAULT_POLL_MS = 15_000;

export function usePlan(options: UsePlanOptions): PlanState {
  const internals = options.internals ?? defaultInternals;
  const { client, planId } = options;
  const live = options.live ?? true;

  const [status, setStatus] = useState<PlanStatus>('loading');
  const [snapshot, setSnapshot] = useState<PlanSnapshot | null>(null);
  const [posting, setPosting] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);

  const generation = useRef(0);
  const inFlight = useRef(false);
  const again = useRef(false);
  const depth = useRef(1);
  const held = useRef<PlanSnapshot | null>(null);
  const alive = useRef(true);
  /**
   * `threadId` -> the highest seq `markRead` has ever had CONFIRMED by the server, whether
   * or not that particular response is the last one to actually land. Two mark-read calls
   * for the same thread can resolve out of order — a slow one for an old tip, after a
   * faster one for a newer tip the reader has since reached — and without this, the OLDER
   * response finishing second would recompute "unread" against its own lower seq and could
   * reopen a badge the newer response had already, correctly, cleared. Mirrors the server's
   * own `greatest()` on `thread_participant.last_read_seq`, client-side, over the same
   * responses this client itself already knows to be authoritative.
   */
  const confirmedSeq = useRef(new Map<string, bigint>());
  // A load that failed on a stale bearer while a snapshot was held. The held snapshot
  // stays on screen and the screen says nothing — but the vendor's next token publish
  // cures exactly this failure, so the retry effect below spends the flag on one more
  // load. One flag, not a count: however many loads fail before the publish, one read
  // after it is current.
  const staleBearer = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (inFlight.current) {
      again.current = true;
      return;
    }
    inFlight.current = true;
    const mine = (generation.current += 1);
    try {
      const next = await internals.loadSnapshot(client, planId, depth.current);
      if (!alive.current || mine !== generation.current) {
        return;
      }
      depth.current = next.depth;
      held.current = next;
      staleBearer.current = false;
      setSnapshot(next);
      setStatus('ready');
    } catch (error) {
      if (!alive.current || mine !== generation.current) {
        return;
      }
      // A plan that has been read once stays on screen. The only thing a later failure
      // changes is whether this client believes it is up to date.
      if (held.current !== null) {
        // A 401 here is remembered, though: the bearer is stale, every read will fail the
        // same way until the vendor publishes a fresh token, and the publish is an event
        // the retry effect below can spend on one more load. This is the comeback's own
        // failure mode — a tab hidden long enough for its token to expire refetches on
        // becoming visible BEFORE the throttled refresh loop has replaced it. The last
        // failure wins the flag, so a plain outage does not leave a stale claim behind.
        staleBearer.current = error instanceof ApiFailure && error.status === 401;
        return;
      }
      // Refused only when the server LOOKED at the request and refused it: the
      // deliberately uniform 404, or a 400. NAMED, not derived, for the same reason as
      // the invitation screen's split. A 401 alone is `waiting` — after a machine
      // sleeps, the stored bearer is expired until the vendor's refresh loop publishes
      // a fresh one, and "isn't available to you" would report that minute as revoked
      // access. Everything else unnamed — a rate limit, a server error, a body that
      // would not decode — falters instead: same retry triggers below, but the screen
      // must not promise that signing in is what is happening.
      if (error instanceof ApiFailure && (error.status === 404 || error.status === 400)) {
        setStatus('refused');
      } else if (error instanceof ApiFailure && error.code === 'unreachable') {
        setStatus('unreachable');
      } else if (error instanceof ApiFailure && error.status === 401) {
        setStatus('waiting');
      } else {
        setStatus('faltered');
      }
    } finally {
      inFlight.current = false;
      if (again.current && alive.current) {
        again.current = false;
        void load();
      }
    }
  }, [client, planId, internals]);

  // A change of plan is a fresh everything. Nothing about the previous plan may survive
  // into the next one's screen, including its depth.
  useEffect(() => {
    depth.current = 1;
    held.current = null;
    setSnapshot(null);
    setStatus('loading');
    void load();
  }, [load]);

  // A fresh bearer and a returning network are each a reason to read again — while
  // nothing has rendered, or when the last read failed on a stale bearer. Once a snapshot
  // is held, the stream and the poll keep it current, and the vendor republishes a token
  // every 50 seconds, which must not become a third refetch schedule — so with a snapshot
  // held, a trigger refetches only when it cures something: `staleBearer`, consumed here
  // and re-armed only by another 401. A trigger during an in-flight load folds into the
  // `again` flag above rather than racing it.
  useEffect(() => {
    const retry = (): void => {
      if (held.current === null || staleBearer.current) {
        staleBearer.current = false;
        void load();
      }
    };
    const unsubscribe = session.subscribe(retry);
    window.addEventListener('online', retry);
    return () => {
      unsubscribe();
      window.removeEventListener('online', retry);
    };
  }, [load]);

  const appendMessage = useCallback(
    (message: Message) => {
      setSnapshot((current) => {
        if (current === null) {
          return current;
        }
        const next = Object.freeze({
          ...current,
          messages: internals.afterPost(current.messages, message),
        });
        held.current = next;
        return next;
      });
    },
    [internals],
  );

  /**
   * Reports the reader's own read position on `threadId`, and flips that thread's local
   * `unread` flag as soon as the server confirms it — rather than waiting for the next poll
   * or signal refetch, which could be up to `DEFAULT_POLL_MS` away.
   *
   * The network call is fire-and-forget from the CALLER's side — nothing here is awaited by
   * whoever calls `markRead` — but the local patch below still waits on it, and is guarded
   * by the load generation counter `load` itself uses: a slow-arriving response must not
   * clobber a snapshot that a newer load already replaced with the truth. That guard alone
   * is not enough, though — it only catches a snapshot REPLACED wholesale by `load()`, and
   * `usePlan` does not even hold the message list for every thread this can be called for:
   * `PrivateChat`'s own thread is read by `usePrivateThread`, entirely outside this
   * snapshot. So the patch does not blindly trust its own `seq` at all — `newestKnownSeq`
   * is `plan/useMarkThreadRead.ts`'s own live answer, for whichever thread it is tracking,
   * to "what is the newest message already known", read at the moment this response
   * actually lands rather than assumed from what was reported at the time it was sent. The
   * badge clears only when nothing newer is known, and is recomputed `true` otherwise,
   * rather than leaving a stale `false` a moment before a fresher `true` would have arrived
   * anyway — correct for the group thread `usePlan` itself loads and for a private thread it
   * has never seen a message from, by the same mechanism. `PlanSnapshot` holds both a
   * `threads` list and a separately selected current `thread` (`snapshot.ts:62-67`), so both
   * are patched together — leaving one stale would make the tab badge and the thread
   * actually on screen disagree about the same fact.
   */
  const markRead = useCallback(
    (threadId: string, seq: string, newestKnownSeq: () => string | undefined): void => {
      const mine = generation.current;
      client
        .markThreadRead(threadId, seq)
        .then(() => {
          if (!alive.current) {
            return;
          }
          // Recorded regardless of whether THIS response is stale enough to skip the patch
          // below — a confirmation the server actually gave must never be forgotten just
          // because a fresher load is about to run anyway, or a later, even-more-stale
          // response arrives after this one and must not regress past it either.
          const reported = BigInt(seq);
          const previouslyConfirmed = confirmedSeq.current.get(threadId) ?? -1n;
          const highWater =
            reported > previouslyConfirmed ? reported : previouslyConfirmed;
          confirmedSeq.current.set(threadId, highWater);
          if (mine !== generation.current) {
            return;
          }
          setSnapshot((current) => {
            if (current === null) {
              return current;
            }
            const newest = newestKnownSeq();
            const stillUnread = newest !== undefined && BigInt(newest) > highWater;
            const patched = (thread: Thread): Thread =>
              thread.id === threadId
                ? Object.freeze({ ...thread, unread: stillUnread })
                : thread;
            const next = Object.freeze({
              ...current,
              threads: Object.freeze(current.threads.map(patched)),
              thread: current.thread === null ? null : patched(current.thread),
            });
            held.current = next;
            return next;
          });
        })
        .catch(() => undefined);
    },
    [client],
  );

  const target = useMemo(
    () => ({ planId, refetch: () => void load(), appendMessage }),
    [planId, load, appendMessage],
  );

  useEffect(() => {
    if (!live) {
      return undefined;
    }
    const stream = internals.openStream({
      baseUrl: client.baseUrl,
      // Read at connect time, so a reconnection picks up the current token.
      token: () => client.bearer(),
      // The same transport every other request goes through. Not `globalThis.fetch`: the
      // client is constructed with one and a stream that reached past it would be talking
      // to a different server than the rest of the application.
      fetchImpl: client.transport,
      onSignal: (signalled) => internals.onSignal(signalled, target),
      // The stream became live. Anything that changed before the server snapshotted this
      // connection is inside what this read returns; anything after it produces a signal.
      // That is the whole of why there is no replay and none is needed.
      onConnected: () => void load(),
      // A DROPPED STREAM IS NOT SHOWN, and nothing here records that it happened.
      //
      // There was a `connected` boolean on this state for a while and nothing rendered it,
      // which is the tell. Losing the connection means the screen stops updating on its
      // own; it does not mean anything is wrong, and a family reading a conversation about
      // a parent's death should not be given a status bar because a proxy timed out. The
      // stream reconnects, the reconnect refetches, and the poll covers the gap meanwhile.
      onDisconnected: () => undefined,
    });
    return () => stream.close();
  }, [client, internals, live, load, target]);

  // The tick. It reads; it decides nothing. See the header of this file.
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;
  useEffect(() => {
    if (!live || pollMs <= 0) {
      return undefined;
    }
    const timer = setInterval(() => {
      // A background tab is a person who is not reading. Nothing about this plan needs to
      // be current while nobody is looking at it; the effect below catches everything up
      // when they come back.
      if (document.visibilityState === 'hidden') {
        return;
      }
      void load();
    }, pollMs);
    return () => clearInterval(timer);
  }, [live, pollMs, load]);

  // The comeback. The ticks skipped while the page was hidden are paid the moment the
  // person looks again. The stream's reconnect refetch is not enough to promise that: a
  // stream that dropped while the page was hidden reconnects on a backoff the browser
  // throttles in hidden tabs, and a reader who comes back before it fires is watching a
  // screen only the next tick — up to a full interval away — would update. Seen live as
  // issue #43: a private chat left behind another window missed the facilitator's reply,
  // and only a manual reload showed it.
  useEffect(() => {
    if (!live) {
      return undefined;
    }
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'hidden') {
        void load();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [live, load]);

  const post = useCallback(
    async (body: string): Promise<void> => {
      const thread = held.current?.thread;
      if (thread === undefined || thread === null) {
        return;
      }
      setPosting(true);
      setSendFailed(false);
      try {
        const stored = await client.postMessage(thread.id, body);
        appendMessage(stored);
        void load();
      } catch {
        setSendFailed(true);
        throw new Error('the message was not sent');
      } finally {
        setPosting(false);
      }
    },
    [client, appendMessage, load],
  );

  const resolve = useCallback(
    async (actionId: string, resolution: string): Promise<void> => {
      await client.resolveAction(actionId, resolution);
      void load();
    },
    [client, load],
  );

  const loadOlder = useCallback(() => {
    if (held.current?.nextBefore === null || held.current === null) {
      return;
    }
    depth.current += 1;
    void load();
  }, [load]);

  return {
    status,
    snapshot,
    posting,
    sendFailed,
    refetch: () => void load(),
    loadOlder,
    post,
    resolve,
    markRead,
  };
}
